/**
 * CoachPlaybook - ToolStrip
 * Version: 0.6.0
 * Description: Minimal vertical toolbar for Playbook Studio surface tools
 */

import React from 'react';

const ACCENT = '#3b82f6';
const ACCENT_HOVER = '#2563eb';

const icons = {
  select: (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <path
        d="M3 1L3 12L6.5 8.5L10 13L12 12L8.5 7.5L13 7.5L3 1Z"
        fill="currentColor"
      />
    </svg>
  ),
  move: (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <path d="M8 2L10 4.5H9V7H11.5V5.5L14 8L11.5 10.5V9H9V11.5H10L8 14L6 11.5H7V9H4.5V10.5L2 8L4.5 5.5V7H7V4.5H6L8 2Z"
        fill="currentColor" />
    </svg>
  ),
  eraser: (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <path
        d="M10.5 2L14 5.5L7.5 12H3L1.5 10.5L10.5 2Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <line x1="6" y1="6.5" x2="10" y2="10.5" stroke="currentColor" strokeWidth="1.5" />
      <line x1="3" y1="14" x2="13" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  undo: (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <path
        d="M4 7L2 5L4 3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2 5H10C12.2091 5 14 6.79086 14 9C14 11.2091 12.2091 13 10 13H7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  redo: (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <path
        d="M12 7L14 5L12 3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 5H6C3.79086 5 2 6.79086 2 9C2 11.2091 3.79086 13 6 13H9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  shield: (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <path
        d="M8 1.5L2.5 4V8C2.5 11.5 8 14.5 8 14.5C8 14.5 13.5 11.5 13.5 8V4L8 1.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  ),
  grid: (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="2" width="12" height="12" rx="1" stroke="currentColor" strokeWidth="1.5" />
      <line x1="6" y1="2" x2="6" y2="14" stroke="currentColor" strokeWidth="1.5" />
      <line x1="10" y1="2" x2="10" y2="14" stroke="currentColor" strokeWidth="1.5" />
      <line x1="2" y1="6" x2="14" y2="6" stroke="currentColor" strokeWidth="1.5" />
      <line x1="2" y1="10" x2="14" y2="10" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  ),
  gear: (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M8 1V3M8 13V15M1 8H3M13 8H15M3.05 3.05L4.46 4.46M11.54 11.54L12.95 12.95M12.95 3.05L11.54 4.46M4.46 11.54L3.05 12.95"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
};

const styles = {
  strip: {
    position: 'absolute',
    left: 6,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 34,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    padding: '5px 3px',
    background: 'rgba(15, 23, 42, 0.85)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderRadius: 10,
    border: '1px solid rgba(255, 255, 255, 0.08)',
    zIndex: 20,
    userSelect: 'none',
  },
  divider: {
    width: 18,
    height: 1,
    background: 'rgba(255, 255, 255, 0.1)',
    margin: '1px 0',
    flexShrink: 0,
  },
};

const getButtonStyle = (isActive, isDisabled, isHovered) => ({
  width: 28,
  height: 28,
  borderRadius: '50%',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: isDisabled ? 'default' : 'pointer',
  color: isActive ? '#fff' : 'rgba(255, 255, 255, 0.7)',
  background: isActive
    ? ACCENT
    : isHovered && !isDisabled
      ? 'rgba(255, 255, 255, 0.12)'
      : 'rgba(255, 255, 255, 0.06)',
  opacity: isDisabled ? 0.3 : 1,
  transition: 'background 0.15s, opacity 0.15s, color 0.15s',
  padding: 0,
  flexShrink: 0,
});

const ToolButton = ({ id, icon, active, disabled, title, onClick }) => {
  const [hovered, setHovered] = React.useState(false);

  return (
    <button
      style={getButtonStyle(active, disabled, hovered)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => !disabled && onClick(id)}
      title={title}
      disabled={disabled}
    >
      {icon}
    </button>
  );
};

const ToolStrip = (props) => {
  const {
    activeTool = 'select',
    showDefenders = true,
    showGrid = false,
    canUndo = false,
    canRedo = false,
    hideDefenderToggle = false,
    onToolChange = () => {},
    onToggleDefenders = () => {},
    onToggleGrid = () => {},
    onUndo = () => {},
    onRedo = () => {},
    onSettings = () => {},
  } = props;
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  if (isMobile) return null;
  return (
    <div style={styles.strip}>
    <ToolButton
      id="select"
      icon={icons.select}
      active={activeTool === 'select'}
      title="Select (V)"
      onClick={() => onToolChange('select')}
    />
    <ToolButton
      id="move"
      icon={icons.move}
      active={activeTool === 'move'}
      title="Move Player (M)"
      onClick={() => onToolChange('move')}
    />
    <ToolButton
      id="eraser"
      icon={icons.eraser}
      active={activeTool === 'eraser'}
      title="Eraser (E)"
      onClick={() => onToolChange('eraser')}
    />

    <div style={styles.divider} />

    <ToolButton
      id="undo"
      icon={icons.undo}
      disabled={!canUndo}
      title="Undo (Cmd+Z)"
      onClick={onUndo}
    />
    <ToolButton
      id="redo"
      icon={icons.redo}
      disabled={!canRedo}
      title="Redo (Cmd+Shift+Z)"
      onClick={onRedo}
    />

    <div style={styles.divider} />

    {!hideDefenderToggle && (
      <ToolButton
        id="defenders"
        icon={icons.shield}
        active={showDefenders}
        title="Toggle Defense (D)"
        onClick={onToggleDefenders}
      />
    )}
    <ToolButton
      id="grid"
      icon={icons.grid}
      active={showGrid}
      title="Toggle Grid (G)"
      onClick={onToggleGrid}
    />

    <div style={styles.divider} />

    <ToolButton
      id="settings"
      icon={icons.gear}
      title="Settings"
      onClick={onSettings}
    />
  </div>
  );
};

export default ToolStrip;
